import { useEffect, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'app/modules/react-redux';
import * as Actions from 'app/store/actions';
import { getProgressToasts } from 'app/store/reducers/progressToasts.reducer';
import ProgressToast from './ProgressToast';

const AUTO_DISMISS_MS = 4500;

const useStyles = makeStyles(() => ({
	stack: {
		position: 'fixed',
		top: 152,
		right: 24,
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		zIndex: 1250,
		pointerEvents: 'none',
	},
	stackItem: {
		pointerEvents: 'auto',
	},
}));

const ProgressToastStack = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const toasts = useSelector(getProgressToasts);
	const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

	useEffect(() => {
		const timers = timersRef.current;
		toasts.forEach((toast) => {
			if ((toast.status === 'done' || toast.status === 'failed') && !timers.has(toast.id)) {
				const handle = setTimeout(() => {
					dispatch(Actions.dismissProgressToast(toast.id));
					timers.delete(toast.id);
				}, AUTO_DISMISS_MS);
				timers.set(toast.id, handle);
			}
		});
		const liveIds = new Set(toasts.map((t) => t.id));
		Array.from(timers.entries()).forEach(([id, handle]) => {
			if (!liveIds.has(id)) {
				clearTimeout(handle);
				timers.delete(id);
			}
		});
	}, [toasts, dispatch]);

	useEffect(
		() => () => {
			timersRef.current.forEach((handle) => clearTimeout(handle));
			timersRef.current.clear();
		},
		[],
	);

	if (toasts.length === 0) return null;

	return (
		<div className={classes.stack}>
			{toasts.map((toast) => (
				<div key={toast.id} className={classes.stackItem}>
					<ProgressToast toast={toast} />
				</div>
			))}
		</div>
	);
};

export default ProgressToastStack;
